import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Form, Overlay, Popover } from "react-bootstrap";
import { toast } from "react-hot-toast";
import BarcodeGenerator from "../components/BarcodeGenerator";
import CancelTagModal from "../components/CancelTagModal";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";

import { createErpSocket } from "../utils/socketIo";
import { listProducts } from "../erpClient";
import {
  isSocketForCurrentBranch,
  normalizeTagCancelledPayload,
  dispatchDashboardPoolInvalidate,
} from "../utils/socketPoolEvents";

const buildImageCandidates = (imagePath) => {
  if (!imagePath) return [];
  const raw = String(imagePath).trim();
  if (!raw) return [];
  if (/^https?:\/\//i.test(raw)) return [raw];

  const normalizedPath =
    raw.includes("/") || raw.startsWith("uploads/")
      ? raw
      : `uploads/product/${raw}`;
  const cleanPath = normalizedPath.replace(/^\/+/, "");
  const imageBase = (process.env.REACT_APP_IMAGE_BASE_URL || "").replace(
    /\/+$/,
    "",
  );
  if (!imageBase) return [cleanPath];
  return [`${imageBase}/${cleanPath}`];
};

/** Map API / socket product record to a table row (same shape as listProducts). */
function mapProductRecordToStockItem(p) {
  const proType = p?.pro_type ?? "";
  const imagePath = p?.image || "";
  const imageCandidates = buildImageCandidates(imagePath);
  const imageUrl = imageCandidates[0] || "";
  const gwt = p?.gwt ?? p?.GWT ?? "";
  const lwtVepari = p?.lwt_vepari ?? p?.LWt_vepari ?? p?.LWT_vepari ?? "";
  const lwtCustomer =
    p?.lwt_customer ?? p?.LWt_customer ?? p?.LWT_customer ?? "";
  const nwt = p?.nwt ?? p?.NWT ?? "";
  const lbrType = p?.lbr_type ?? p?.LBR_type ?? "";
  const lbrPercent = p?.lbr_percent ?? p?.LBR ?? "";
  const lbrRate = p?.lbr_rate ?? p?.LBR_rate ?? "";
  const lbrAmt = p?.lbr_amt ?? p?.LBR_amt ?? "";
  const othAmt = p?.oth_amt ?? p?.othamt ?? "";
  const mrp = p?.mrp ?? p?.MRP ?? "";
  return {
    id: p?.id ?? "",
    productGroupId: p?.product_group_id ?? p?.product_group ?? "",
    productGroup:
      p?.product_group_name ??
      p?.product_group?.name ??
      p?.product_group ??
      "Unknown Group",
    design: p?.design ?? "",
    tagNo: p?.tag_no ?? "",
    size: p?.size ?? "",
    pcs: p?.pcs ?? "",
    gWt: gwt,
    lWtVepari: lwtVepari,
    lWtCustomer: lwtCustomer,
    lWt: lwtCustomer || lwtVepari || "",
    nwt,
    ltype: lbrType,
    lbrPercentage: lbrPercent,
    lbrRate,
    lbrAmt,
    othAmt,
    mrp,
    huid: p?.huid ?? "",
    image: imageUrl,
    imageCandidates,
    supplierId: p?.supplier_id ?? p?.Supplier ?? "",
    supplier:
      p?.supplier_company_name ??
      p?.supplier_name ??
      p?.supplier?.company_name ??
      p?.supplier?.name ??
      p?.karigar_company_name ??
      "",
    status: p?.status ?? "",
    createdAt: p?.created_at ?? "",
    charges: Array.isArray(p?.charges)
      ? p.charges.map((c) => ({
          ...c,
          charge_type: c?.charge_type ?? c?.chargeType ?? "",
          wt: c?.wt ?? c?.weight ?? c?.wtPP ?? "",
          wtPP: c?.wtPP ?? c?.wt ?? c?.weight ?? "",
          wtTotal: c?.wtTotal ?? "",
          ct: c?.ct ?? "",
          con_percent: c?.con_percent ?? c?.conPercentage ?? "",
        }))
      : [],
    pro_type: String(proType),
    current_pcs: p?.current_pcs ?? "",
    current_gwt: p?.current_gwt ?? "",
  };
}

function StockTable() {
  const navigate = useNavigate();
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();
  const [stockItems, setStockItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [stockTypeFilter, setStockTypeFilter] = useState("all");
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);

  // References and state for info popover
  const [showInfoPopover, setShowInfoPopover] = useState(false);
  const [popoverTarget, setPopoverTarget] = useState(null);
  const [infoItem, setInfoItem] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [showCancelTagModal, setShowCancelTagModal] = useState(false);
  const [cancelTagProduct, setCancelTagProduct] = useState(null);
  const [socket, setSocket] = useState(null);

  // Cache for storing previously fetched pages
  const [cachedPages, setCachedPages] = useState({});

  const fetchStockItems = useCallback(
    async (page = 1, limit = 10, search = "", stockType = "all") => {
      // Create a unique cache key based on pagination and filter parameters
      const cacheKey = `${page}-${limit}-${search}-${stockType}`;

      // Check if this page is already cached
      if (cachedPages[cacheKey]) {
        setStockItems(cachedPages[cacheKey].items);
        setTotalCount(cachedPages[cacheKey].total);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const response = await listProducts({
          page,
          limit,
          search,
          stock_type: stockType === "all" ? "" : stockType,
        });

        if (!response?.success) {
          throw new Error(response?.message || "Failed to fetch products");
        }

        const items = Array.isArray(response?.data?.items)
          ? response.data.items
          : [];
        const pagination = response?.data?.pagination || {};
        const totalItems =
          typeof pagination.total === "number"
            ? pagination.total
            : items.length;

        const mappedStockItems = items
          .map((p) => mapProductRecordToStockItem(p))
          .sort((a, b) => Number(b.id) - Number(a.id));

        setStockItems(mappedStockItems);
        setTotalCount(totalItems);

        setCachedPages((prevCache) => ({
          ...prevCache,
          [cacheKey]: {
            items: mappedStockItems,
            total: totalItems,
          },
        }));
      } catch (error) {
        console.error("Error fetching stock items:", error);
        setStockItems([]);
        setTotalCount(0);
        toast.error(
          error.response?.data?.message || "Failed to fetch stock items",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [cachedPages],
  );

  useEffect(() => {
    // Debounce search query
    const timeoutId = setTimeout(
      () => {
        fetchStockItems(
          currentPage,
          itemsPerPage,
          searchQuery,
          stockTypeFilter,
        );
      },
      searchQuery ? 200 : 0,
    ); // 200ms debounce for search, immediate for other changes

    return () => clearTimeout(timeoutId);
  }, [
    currentPage,
    itemsPerPage,
    searchQuery,
    stockTypeFilter,
    fetchStockItems,
  ]);

  // Socket.IO listener for real-time updates
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    // Connection event logging
    newSocket.on("connect", () => {
      console.log("StockTable: Connected to socket server");
    });

    newSocket.on("disconnect", (reason) => {
      console.log("StockTable: Disconnected from socket server:", reason);
    });

    newSocket.on("connect_error", (error) => {
      console.error("StockTable: Socket connection error:", error);
    });

    const mergeProductFromSocket = (p) => {
      if (!p) return;
      const mapped = mapProductRecordToStockItem(p);
      setCachedPages({});
      setStockItems((prev) =>
        prev.map((row) =>
          String(row.id) === String(mapped.id) ? mapped : row,
        ),
      );
    };

    newSocket.on("product_created", (payload) => {
      const p = payload?.product;
      if (!p) return;
      const mapped = mapProductRecordToStockItem(p);
      setCachedPages({});
      let bumpTotal = false;
      setStockItems((prev) => {
        const exists = prev.some((r) => String(r.id) === String(mapped.id));
        if (exists) {
          return prev.map((r) =>
            String(r.id) === String(mapped.id) ? mapped : r,
          );
        }
        bumpTotal = true;
        if (currentPage !== 1) {
          return prev;
        }
        return [mapped, ...prev].slice(0, itemsPerPage);
      });
      if (bumpTotal) {
        setTotalCount((c) => c + 1);
      }
      if (p?.id !== undefined && p?.id !== null) {
        toast.success(`New product added: ${p.id}`);
      }
    });

    newSocket.on("product_updated", (payload) => {
      const p = payload?.product;
      mergeProductFromSocket(p);
      if (p?.id !== undefined && p?.id !== null) {
        toast.success(`Product updated: ${p.id}`);
      }
    });

    const handleTagCancelled = (payload) => {
      if (!isSocketForCurrentBranch(payload)) return;
      const cancel = normalizeTagCancelledPayload(payload);
      if (cancel.product_id == null) return;

      setCachedPages({});
      setStockItems((prev) =>
        prev.map((row) => {
          if (String(row.id) !== String(cancel.product_id)) return row;
          const next = { ...row };
          if (cancel.remaining_nwt != null) {
            next.nwt = cancel.remaining_nwt;
          }
          if (cancel.remaining_pcs != null) {
            next.pcs = cancel.remaining_pcs;
            next.current_pcs = cancel.remaining_pcs;
          }
          if (cancel.product_status != null) {
            next.status = cancel.product_status;
          }
          return next;
        }),
      );

      setSelectedStock((prev) => {
        if (!prev || String(prev.id) !== String(cancel.product_id)) return prev;
        const next = { ...prev };
        if (cancel.remaining_nwt != null) next.nwt = cancel.remaining_nwt;
        if (cancel.remaining_pcs != null) {
          next.pcs = cancel.remaining_pcs;
          next.current_pcs = cancel.remaining_pcs;
        }
        if (cancel.product_status != null) next.status = cancel.product_status;
        return next;
      });

      dispatchDashboardPoolInvalidate();
    };

    newSocket.on("tag_cancelled", handleTagCancelled);

    return () => {
      newSocket.off("product_created");
      newSocket.off("product_updated");
      newSocket.off("tag_cancelled", handleTagCancelled);
      newSocket.disconnect();
    };
  }, [currentPage, itemsPerPage]);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in StockTable, clearing cache and refetching data...",
        event.detail,
      );
      setCachedPages({});
      setCurrentPage(1);
      setSelectedItems([]);
      fetchStockItems(1, itemsPerPage, searchQuery, stockTypeFilter);
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [itemsPerPage, searchQuery, stockTypeFilter, fetchStockItems]);

  const isBulkStockProduct = (stock) =>
    stock != null && String(stock.pro_type ?? "") === "1";

  // Handle Info Click
  const handleInfoClick = (stockItem) => {
    setSelectedStock({
      ...stockItem,
      pro_type: String(stockItem.pro_type ?? ""),
    });
    setShowInfoModal(true);
  };

  // Handle Page Change
  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      // Data will be fetched automatically by useEffect
    }
  };

  // Handle Items Per Page Change
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
    setCachedPages({}); // Clear cache when items per page changes
    // Data will be fetched automatically by useEffect
  };

  // Handle checkbox selection
  const handleCheckboxChange = (itemId) => {
    setSelectedItems((prevSelected) => {
      if (prevSelected.includes(itemId)) {
        return prevSelected.filter((id) => id !== itemId);
      } else {
        return [...prevSelected, itemId];
      }
    });
  };

  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectedItems.length === stockItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(stockItems.map((item) => item.id));
    }
  };

  // Handle bulk actions for selected items
  const handleBulkAction = (action) => {
    if (action === "edit") {
      navigate("/editstock", {
        state: { selectedItemIds: selectedItems },
      });
    } else if (action === "print-barcode") {
      const selectedStockItems = stockItems.filter((item) =>
        selectedItems.includes(item.id),
      );
      if (selectedStockItems.length > 0) {
        navigate("/printbarcode", {
          state: { selectedItems: selectedStockItems },
        });
      } else {
        toast.error("Please select items to print barcodes");
      }
    }
  };

  // No client-side filtering needed - all filtering is done on the backend
  // stockItems already contains the paginated and filtered data from the API

  // Handle info button hover
  const handleInfoHover = (event, item) => {
    setPopoverTarget(event.target);
    setInfoItem(item);
    setShowInfoPopover(true);
  };

  // Handle image click for preview
  const handleImageClick = (e, imageUrl) => {
    e.preventDefault();
    e.stopPropagation();
    setPreviewImage(imageUrl);
    setShowImagePreview(true);
  };

  const handleCancelTagClick = (item) => {
    if (String(item.status || "").toUpperCase() !== "ACTIVE") {
      toast.error("Only active tagged products can be cancelled");
      return;
    }
    setCancelTagProduct(item);
    setShowCancelTagModal(true);
  };

  const handleCancelTagSuccess = (res) => {
    const cancel = res?.data?.cancel ?? res?.data ?? {};
    const productId = cancel.product_id ?? cancelTagProduct?.id;
    if (productId != null) {
      setStockItems((prev) =>
        prev.map((row) => {
          if (String(row.id) !== String(productId)) return row;
          const next = { ...row };
          if (cancel.remaining_nwt != null) next.nwt = cancel.remaining_nwt;
          if (cancel.remaining_pcs != null) {
            next.pcs = cancel.remaining_pcs;
            next.current_pcs = cancel.remaining_pcs;
          }
          if (cancel.product_status != null) next.status = cancel.product_status;
          return next;
        }),
      );
    }
    dispatchDashboardPoolInvalidate();
  };

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Stock Table</h4>
            <div className="d-flex gap-2">
              <button
                className={`btn btn-success ${buttonClass}`}
                onClick={() => navigate("/addstock")}
              >
                Add Stock
              </button>
              <button
                className={`btn btn-success ${buttonClass}`}
                onClick={() => navigate("/bulkstock")}
              >
                Add Bulk Stock
              </button>
            </div>
          </div>

          <div className="card-body p-4">
            {/* Search Section and Bulk Actions */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <input
                type="text"
                className={`form-control ${
                  buttonTableStyle === "square" ? "rounded-0" : ""
                }`}
                style={{ maxWidth: "260px" }}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                  setCachedPages({}); // Clear cache when search changes
                }}
              />
              <div className="d-flex gap-2 align-items-center">
                <Form.Select
                  className={`form-control w-auto ${
                    buttonTableStyle === "square" ? "rounded-0" : ""
                  }`}
                  style={{ maxWidth: "200px" }}
                  value={stockTypeFilter}
                  onChange={(e) => {
                    setStockTypeFilter(e.target.value);
                    setCurrentPage(1);
                    setCachedPages({}); // Clear cache when filter changes
                  }}
                >
                  <option value="all">All Stock</option>
                  <option value="0">Individual Stock</option>
                  <option value="1">Bulk Stock</option>
                </Form.Select>

                {selectedItems.length > 0 && (
                  <div className="d-flex gap-2">
                    <span className="align-self-center">
                      {selectedItems.length} items selected
                    </span>
                    <div className="dropdown">
                      <button
                        className={`btn btn-primary dropdown-toggle ${buttonClass}`}
                        type="button"
                        id="bulkActionDropdown"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        Actions
                      </button>
                      <ul
                        className="dropdown-menu"
                        aria-labelledby="bulkActionDropdown"
                      >
                        <li>
                          <button
                            className="dropdown-item"
                            onClick={() => handleBulkAction("edit")}
                          >
                            Edit Selected
                          </button>
                        </li>
                        {/* <li>
                          <button
                            className="dropdown-item"
                            onClick={() => handleBulkAction("export-to-pdf")}
                          >
                            Export to PDF
                          </button>
                        </li>
                        <li>
                          <button
                            className="dropdown-item"
                            onClick={() => handleBulkAction("export-to-excel")}
                          >
                            Export to Excel
                          </button>
                        </li> */}
                        <li>
                          <button
                            className="dropdown-item"
                            onClick={() => handleBulkAction("print")}
                          >
                            Print
                          </button>
                        </li>
                        <li>
                          <button
                            className="dropdown-item"
                            onClick={() => handleBulkAction("print-barcode")}
                          >
                            Print Barcode
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Table */}
            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table
                className={`table text-nowrap mb-0 align-middle ${tableClass}`}
              >
                <thead className="text-dark fs-4">
                  <tr
                    onMouseEnter={() => setIsHeaderHovered(true)}
                    onMouseLeave={() => setIsHeaderHovered(false)}
                  >
                    <th>
                      <div
                        style={{
                          visibility:
                            isHeaderHovered || selectedItems.length > 0
                              ? "visible"
                              : "hidden",
                        }}
                      >
                        <Form.Check
                          type="checkbox"
                          onChange={handleSelectAll}
                          checked={
                            selectedItems.length === stockItems.length &&
                            stockItems.length > 0
                          }
                          indeterminate={
                            selectedItems.length > 0 &&
                            selectedItems.length < stockItems.length
                          }
                        />
                      </div>
                    </th>
                    <th>Tag No.</th>
                    <th>Product Group</th>
                    <th>NWt</th>
                    <th>LbrAmt</th>
                    <th>OthAmt</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="7" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : stockItems.length > 0 ? (
                    stockItems.map((item) => (
                      <tr
                        key={item.id}
                        onMouseEnter={() => setHoveredRow(item.id)}
                        onMouseLeave={() => setHoveredRow(null)}
                      >
                        <td>
                          <div
                            style={{
                              visibility:
                                hoveredRow === item.id ||
                                selectedItems.includes(item.id)
                                  ? "visible"
                                  : "hidden",
                            }}
                          >
                            <Form.Check
                              type="checkbox"
                              checked={selectedItems.includes(item.id)}
                              onChange={() => handleCheckboxChange(item.id)}
                            />
                          </div>
                        </td>
                        <td>{item.tagNo}</td>
                        <td>{item.productGroup}</td>
                        <td>{item.nwt}</td>
                        <td>{item.lbrAmt}</td>
                        <td>{item.othAmt}</td>
                        <td>
                          <div className="d-flex gap-1 justify-content-center align-items-center">
                            <button
                              type="button"
                              className={`btn btn-info btn-sm ${buttonClass}`}
                              onClick={() => handleInfoClick(item)}
                              title="View details"
                              style={{
                                width: "32px",
                                height: "32px",
                                padding: 0,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Icon
                                icon="solar:info-circle-bold"
                                width="16"
                                height="16"
                              />
                            </button>
                            {item.tagNo ? (
                              <button
                                type="button"
                                className={`btn btn-info btn-sm ${buttonClass}`}
                                onClick={() => handleCancelTagClick(item)}
                                title="Cancel tag / break to pool"
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  padding: 0,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Icon
                                  icon="solar:close-circle-bold"
                                  width="16"
                                  height="16"
                                />
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No stock items found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        </div>
      </div>

      {/* Info Modal */}
      <Modal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-5" style={{ color: "#2c2b2b" }}>
            <div
              className="ps-3"
              style={{ color: "#2c2b2b", fontSize: "1.1rem" }}
            >
              {selectedStock?.productGroup}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedStock && (
            <div className="bg-white rounded overflow-hidden">
              <div className="row g-2">
                {/* Left Column - Image, MRP, and Supplier */}
                <div className="col-md-3">
                  <div className="card">
                    <div className="card-body p-2 text-center">
                      <a
                        href="/"
                        onClick={(e) =>
                          handleImageClick(e, selectedStock.image)
                        }
                        style={{ cursor: "pointer" }}
                      >
                        {selectedStock.image ? (
                          <img
                            src={selectedStock.image}
                            alt={selectedStock.productGroup}
                            className="img-fluid border"
                            style={{ maxHeight: "80px", objectFit: "contain" }}
                            onError={(e) => {
                              const candidates = Array.isArray(
                                selectedStock.imageCandidates,
                              )
                                ? selectedStock.imageCandidates
                                : [];
                              const currentIdx = candidates.indexOf(
                                e.currentTarget.src,
                              );
                              const nextUrl = candidates[currentIdx + 1];
                              if (nextUrl) {
                                e.currentTarget.src = nextUrl;
                                return;
                              }
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <div
                            className="border d-flex align-items-center justify-content-center text-muted small"
                            style={{ height: "80px", width: "100%" }}
                          >
                            No Image
                          </div>
                        )}
                        <div className="small">Click to enlarge</div>
                      </a>
                    </div>
                  </div>

                  {/* MRP Section */}
                  <div
                    className="card mt-2"
                    style={{ border: "1px solid #EFF4FA" }}
                  >
                    <div className="card-header bg-light py-1 ps-3">
                      <small className="fw-bold" style={{ color: "#2c2b2b" }}>
                        MRP
                      </small>
                    </div>
                    <div className="card-body p-2">
                      <div className="small ps-3" style={{ color: "#2c2b2b" }}>
                        {selectedStock.mrp}
                      </div>
                    </div>
                  </div>

                  {/* Supplier Section */}
                  <div
                    className="card mt-2"
                    style={{ border: "1px solid #EFF4FA" }}
                  >
                    <div className="card-header bg-light py-1 ps-3">
                      <small className="fw-bold" style={{ color: "#2c2b2b" }}>
                        Supplier
                      </small>
                    </div>
                    <div className="card-body p-2">
                      <div className="small ps-3" style={{ color: "#2c2b2b" }}>
                        {selectedStock.supplier}
                      </div>
                    </div>
                  </div>

                  {/* HUID Section - Only show if huid exists and is not empty */}
                  {selectedStock.huid && selectedStock.huid.trim() && (
                    <div
                      className="card mt-2"
                      style={{ border: "1px solid #EFF4FA" }}
                    >
                      <div className="card-header bg-light py-1 ps-3">
                        <small className="fw-bold" style={{ color: "#2c2b2b" }}>
                          HUID
                        </small>
                      </div>
                      <div className="card-body p-2">
                        <div
                          className="small ps-3"
                          style={{ color: "#2c2b2b" }}
                        >
                          {selectedStock.huid.split("##").map((huid, index) => (
                            <div key={index} className="mb-1">
                              {index + 1}. {huid.trim()}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Middle Column - Basic Info, Weight, and Labor */}
                <div className="col-md-9">
                  <div className="row g-2">
                    {/* Basic Info Section */}
                    <div className="col-md-4">
                      <div
                        className="card"
                        style={{ border: "1px solid #EFF4FA" }}
                      >
                        <div className="card-header bg-light py-1 ps-3">
                          <small
                            className="fw-bold"
                            style={{ color: "#2c2b2b" }}
                          >
                            Basic Info
                          </small>
                        </div>
                        <div className="card-body p-2">
                          <table className="table table-sm table-borderless mb-0 w-auto">
                            <tbody>
                              <tr>
                                <td className="small">
                                  <div className="d-flex align-items-center">
                                    <span style={{ minWidth: "60px" }}>
                                      Design:
                                    </span>
                                    <span>{selectedStock.design}</span>
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td className="small">
                                  <div className="d-flex align-items-center">
                                    <span style={{ minWidth: "60px" }}>
                                      Pcs:
                                    </span>
                                    <span>
                                      {selectedStock.pro_type === "0"
                                        ? selectedStock.current_pcs
                                        : selectedStock.pcs}
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Weight Section */}
                    <div className="col-md-4">
                      <div
                        className="card"
                        style={{ border: "1px solid #EFF4FA" }}
                      >
                        <div className="card-header bg-light py-1 ps-3">
                          <small
                            className="fw-bold"
                            style={{ color: "#2c2b2b" }}
                          >
                            Weight
                          </small>
                        </div>
                        <div className="card-body p-2">
                          <table className="table table-sm table-borderless mb-0 w-auto">
                            <tbody>
                              <tr>
                                <td className="small">
                                  <div className="d-flex align-items-center">
                                    <span style={{ minWidth: "60px" }}>
                                      GWt:
                                    </span>
                                    <span>{selectedStock.gWt}</span>
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td className="small">
                                  <div className="d-flex align-items-center">
                                    <span style={{ minWidth: "60px" }}>
                                      LWt:
                                    </span>
                                    <span>{selectedStock.lWt}</span>
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td className="small">
                                  <div className="d-flex align-items-center">
                                    <span style={{ minWidth: "60px" }}>
                                      NWt:
                                    </span>
                                    <span>{selectedStock.nwt}</span>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Labor Section */}
                    <div className="col-md-4">
                      <div
                        className="card"
                        style={{ border: "1px solid #EFF4FA" }}
                      >
                        <div className="card-header bg-light py-1 ps-3 d-flex justify-content-between align-items-center">
                          <small
                            className="fw-bold"
                            style={{ color: "#2c2b2b" }}
                          >
                            Labor
                          </small>
                          <div className="text-end">
                            <small style={{ color: "#2c2b2b" }}>Amt:</small>
                            <span
                              className="small fw-bold ms-2"
                              style={{ color: "#2c2b2b" }}
                            >
                              {selectedStock.lbrAmt}
                            </span>
                          </div>
                        </div>
                        <div className="card-body p-2">
                          <table className="table table-sm table-borderless mb-0 w-auto">
                            <tbody>
                              <tr>
                                <td className="small">
                                  <div className="d-flex align-items-center">
                                    <span style={{ minWidth: "60px" }}>
                                      LType:
                                    </span>
                                    <span>{selectedStock.ltype || "N/A"}</span>
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td className="small">
                                  <div className="d-flex align-items-center">
                                    <span style={{ minWidth: "60px" }}>
                                      Lbr%:
                                    </span>
                                    <span>{selectedStock.lbrPercentage}%</span>
                                  </div>
                                </td>
                              </tr>
                              <tr>
                                <td className="small">
                                  <div className="d-flex align-items-center">
                                    <span style={{ minWidth: "60px" }}>
                                      Lbr Rate:
                                    </span>
                                    <span>{selectedStock.lbrRate}</span>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Other Charges Section */}
                  {selectedStock.charges &&
                    selectedStock.charges.length > 0 && (
                      <div className="row g-2 mt-2">
                        <div className="col-12">
                          <div
                            className="card"
                            style={{ border: "1px solid #EFF4FA" }}
                          >
                            <div className="card-header bg-light py-1 ps-3 d-flex justify-content-between align-items-center">
                              <small
                                className="fw-bold"
                                style={{ color: "#2c2b2b" }}
                              >
                                Other Charges
                              </small>
                              <div className="text-end">
                                <small style={{ color: "#2c2b2b" }}>Amt:</small>
                                <span
                                  className="small fw-bold ms-2"
                                  style={{ color: "#2c2b2b" }}
                                >
                                  {selectedStock.othAmt}
                                </span>
                              </div>
                            </div>
                            <div className="card-body p-2">
                              <table className="table table-sm table-borderless mb-0">
                                <tbody>
                                  {selectedStock.charges.map(
                                    (charge, index) => {
                                      const paramCount = [
                                        charge.wt && parseFloat(charge.wt) > 0,
                                        charge.ct && parseFloat(charge.ct) > 0,
                                        charge.pcs &&
                                          parseFloat(charge.pcs) > 0,
                                        charge.con_percent &&
                                          parseFloat(charge.con_percent) > 0,
                                      ].filter(Boolean).length;

                                      let currentParam = 0;

                                      return (
                                        <tr key={index}>
                                          <td className="small" width="40%">
                                            <div className="d-flex flex-column">
                                              <span>{charge.charge_type}</span>
                                              <div className="d-flex flex-wrap gap-1 align-items-center">
                                                {charge.wt &&
                                                  parseFloat(charge.wt) > 0 && (
                                                    <>
                                                      <span>
                                                        wt: {charge.wt}
                                                      </span>
                                                      {++currentParam <
                                                        paramCount && (
                                                        <span>|</span>
                                                      )}
                                                    </>
                                                  )}
                                                {charge.ct &&
                                                  parseFloat(charge.ct) > 0 && (
                                                    <>
                                                      <span>
                                                        ct: {charge.ct}
                                                      </span>
                                                      {++currentParam <
                                                        paramCount && (
                                                        <span>|</span>
                                                      )}
                                                    </>
                                                  )}
                                                {charge.pcs &&
                                                  parseFloat(charge.pcs) >
                                                    0 && (
                                                    <>
                                                      <span>
                                                        pcs: {charge.pcs}
                                                      </span>
                                                      {++currentParam <
                                                        paramCount && (
                                                        <span>|</span>
                                                      )}
                                                    </>
                                                  )}
                                                {charge.con_percent &&
                                                  parseFloat(
                                                    charge.con_percent,
                                                  ) > 0 && (
                                                    <span>
                                                      con: {charge.con_percent}%
                                                    </span>
                                                  )}
                                              </div>
                                            </div>
                                          </td>
                                          <td className="small" width="40%">
                                            <div className="d-flex gap-2">
                                              <span>Rate on:</span>
                                              <span>{charge.rate_on}</span>
                                              {charge.rate && (
                                                <>
                                                  <span>Rate:</span>
                                                  <span>{charge.rate}</span>
                                                </>
                                              )}
                                            </div>
                                          </td>
                                          <td
                                            className="small text-end"
                                            width="20%"
                                          >
                                            <span className="fw-bold">
                                              {charge.amount}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    },
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        {isBulkStockProduct(selectedStock) && (
          <Modal.Footer>
            <div className="w-100 d-flex justify-content-end text-danger small">
              <span className="me-4">
                Current Pcs: {selectedStock?.current_pcs || "0"}
              </span>
              <span>Current GWt: {selectedStock?.current_gwt || "0"}</span>
            </div>
          </Modal.Footer>
        )}
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        show={showImagePreview}
        onHide={() => setShowImagePreview(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {previewImage && (
            <img
              src={previewImage}
              alt="Product Preview"
              className="img-fluid"
              style={{ maxHeight: "70vh" }}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Add BarcodeGenerator Modal */}
      <BarcodeGenerator
        show={showBarcodeModal}
        onHide={() => setShowBarcodeModal(false)}
        selectedItems={stockItems.filter((item) =>
          selectedItems.includes(item.id),
        )}
      />

      <CancelTagModal
        show={showCancelTagModal}
        onHide={() => {
          setShowCancelTagModal(false);
          setCancelTagProduct(null);
        }}
        product={cancelTagProduct}
        buttonClass={buttonClass}
        onSuccess={handleCancelTagSuccess}
      />
    </div>
  );
}

export default StockTable;
